import axios from "axios";
import { DataTable } from "simple-datatables";
import { showAlert, showConfirm, handleRequestError } from "../../utils/alert";

export default () => ({
  // Core State
  hasAccessModule: true,
  activeTab: "users", // 'users', 'roles', 'permissions'

  // Modal States
  isUserModalOpen: false,
  isRoleModalOpen: false,
  isPermissionModalOpen: false,
  isSubmitting: false,
  editMode: false,
  editId: null,

  // Data Storage
  usersList: [],
  rolesList: [],
  permissionsList: [],

  // Form Payloads
  userForm: { name: "", email: "", password: "", role_ids: [] },
  roleForm: { name: "", slug: "", permission_ids: [] },
  permissionForm: { name: "", slug: "" },

  // DataTable Instances
  usersDataTable: null,
  rolesDataTable: null,
  permissionsDataTable: null,

  async initAdminUsers() {
    const token = localStorage.getItem("admin_jwt_token");
    if (!token) {
      window.location.href = "/admin/login";
      return;
    }

    window.axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

    try {
      // Como o backend já protege as rotas, fazemos a requisição direta.
      // Qualquer falha de permissão resultará em um erro 403 que será capturado abaixo.
      await Promise.all([
        this.loadPermissionsList(),
        this.loadRolesList(),
        this.loadUsersTable(),
      ]);

      this.initRolesTable();
      this.initPermissionsTable();
    } catch (error) {
      // Se a API retornar 403 Forbidden, o usuário não tem permissão para visualizar
      if (error.response && error.response.status === 403) {
        this.hasAccessModule = false;
        return;
      }

      if (error.response && [401, 402].includes(error.response.status)) return;
      console.error("Infrastructure error:", error);
    }
  },

  // ================= TABS =================

  switchTab(tab) {
    this.activeTab = tab;
    // Small delay to allow Alpine to change DOM display before Datatable tries to adjust widths
    setTimeout(() => {
      if (tab === "users" && this.usersDataTable) this.usersDataTable.update();
      if (tab === "roles" && this.rolesDataTable) this.rolesDataTable.update();
      if (tab === "permissions" && this.permissionsDataTable)
        this.permissionsDataTable.update();
    }, 50);
  },

  // ================= DATA FETCHING =================
  // Blocos try/catch removidos para que os erros subam para o initAdminUsers e bloqueiem o acesso corretamente

  async loadPermissionsList() {
    const response = await axios.get("/api/admin/admin-permissions");
    this.permissionsList = response.data.data;
    if (this.permissionsDataTable) this.initPermissionsTable();
  },

  async loadRolesList() {
    const response = await axios.get("/api/admin/admin-roles");
    this.rolesList = response.data.data;
    if (this.rolesDataTable) this.initRolesTable();
  },

  async loadUsersTable() {
    const response = await axios.get("/api/admin/users");
    this.usersList = response.data.data;

    if (this.usersDataTable) this.usersDataTable.destroy();

    const tbody = document.querySelector("#adminUsersTable tbody");
    if (!tbody) return;

    tbody.innerHTML = this.usersList
      .map(
        (user) => `
      <tr>
        <td class="py-4 px-4 xl:px-6"><p class="font-medium text-slate-800">${user.name}</p></td>
        <td class="py-4 px-4 xl:px-6 text-slate-600">${user.email}</td>
        <td class="py-4 px-4 xl:px-6 text-slate-500 text-sm">
          ${user.role_names || '<span class="text-slate-400 italic">Nenhum</span>'}
        </td>
        <td class="text-left py-4 px-4 xl:px-6">
          <div class="flex justify-start gap-2">
            <button onclick="window.dispatchEvent(new CustomEvent('edit-admin-user', {detail: ${user.id}}))" class="rounded-md bg-orange-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-orange-600">Editar</button>
            <button onclick="window.dispatchEvent(new CustomEvent('delete-admin-user', {detail: ${user.id}}))" class="rounded-md bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700">Excluir</button>
          </div>
        </td>
      </tr>
    `,
      )
      .join("");

    this.usersDataTable = new DataTable(
      "#adminUsersTable",
      this.getDatatableConfig("Nenhum usuário encontrado"),
    );
  },

  initRolesTable() {
    if (this.rolesDataTable) this.rolesDataTable.destroy();
    const tbody = document.querySelector("#adminRolesTable tbody");
    if (!tbody) return;

    tbody.innerHTML = this.rolesList
      .map(
        (role) => `
      <tr>
        <td class="py-4 px-4 xl:px-6"><p class="font-medium text-slate-800">${role.name}</p></td>
        <td class="py-4 px-4 xl:px-6 text-slate-600">${role.slug}</td>
        <td class="py-4 px-4 xl:px-6 text-slate-500 text-sm">
            ${role.permission_names || '<span class="text-slate-400 italic">Nenhuma</span>'}
        </td>
        <td class="text-left py-4 px-4 xl:px-6">
          <div class="flex justify-start gap-2">
            <button onclick="window.dispatchEvent(new CustomEvent('edit-admin-role', {detail: ${role.id}}))" class="rounded-md bg-orange-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-orange-600">Editar</button>
            <button onclick="window.dispatchEvent(new CustomEvent('delete-admin-role', {detail: ${role.id}}))" class="rounded-md bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700">Excluir</button>
          </div>
        </td>
      </tr>
    `,
      )
      .join("");

    this.rolesDataTable = new DataTable(
      "#adminRolesTable",
      this.getDatatableConfig("Nenhum cargo encontrado"),
    );
  },

  initPermissionsTable() {
    if (this.permissionsDataTable) this.permissionsDataTable.destroy();
    const tbody = document.querySelector("#adminPermissionsTable tbody");
    if (!tbody) return;

    tbody.innerHTML = this.permissionsList
      .map(
        (perm) => `
      <tr>
        <td class="py-4 px-4 xl:px-6"><p class="font-medium text-slate-800">${perm.name}</p></td>
        <td class="py-4 px-4 xl:px-6 text-slate-600"><code>${perm.slug}</code></td>
        <td class="text-left py-4 px-4 xl:px-6">
          <div class="flex justify-start gap-2">
            <button onclick="window.dispatchEvent(new CustomEvent('edit-admin-permission', {detail: ${perm.id}}))" class="rounded-md bg-orange-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-orange-600">Editar</button>
            <button onclick="window.dispatchEvent(new CustomEvent('delete-admin-permission', {detail: ${perm.id}}))" class="rounded-md bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700">Excluir</button>
          </div>
        </td>
      </tr>
    `,
      )
      .join("");

    this.permissionsDataTable = new DataTable(
      "#adminPermissionsTable",
      this.getDatatableConfig("Nenhuma permissão encontrada"),
    );
  },

  getDatatableConfig(noRowsMsg) {
    return {
      searchable: false,
      perPageSelect: false,
      labels: {
        info: "Mostrando de {start} até {end} de {rows} registos",
        noRows: noRowsMsg,
        noResults: "Nenhum resultado encontrado",
      },
    };
  },

  // ================= MODAL HANDLERS =================

  // --- Users ---
  openCreateUser() {
    this.editMode = false;
    this.userForm = { name: "", email: "", password: "", role_ids: [] };
    this.isUserModalOpen = true;
  },
  openEditUser(id) {
    this.editMode = true;
    this.editId = id;
    const user = this.usersList.find((u) => u.id === id);
    this.userForm = {
      name: user.name,
      email: user.email,
      password: "",
      role_ids: user.role_ids || [],
    };
    this.isUserModalOpen = true;
  },

  // --- Roles ---
  openCreateRole() {
    this.editMode = false;
    this.roleForm = { name: "", slug: "", permission_ids: [] };
    this.isRoleModalOpen = true;
  },
  openEditRole(id) {
    this.editMode = true;
    this.editId = id;
    const role = this.rolesList.find((r) => r.id === id);
    this.roleForm = {
      name: role.name,
      slug: role.slug,
      permission_ids: role.permission_ids || [],
    };
    this.isRoleModalOpen = true;
  },

  // --- Permissions ---
  openCreatePermission() {
    this.editMode = false;
    this.permissionForm = { name: "", slug: "" };
    this.isPermissionModalOpen = true;
  },
  openEditPermission(id) {
    this.editMode = true;
    this.editId = id;
    const perm = this.permissionsList.find((p) => p.id === id);
    this.permissionForm = { name: perm.name, slug: perm.slug };
    this.isPermissionModalOpen = true;
  },

  // ================= SUBMIT ACTIONS =================
  // Os blocos try/catch continuam aqui pois ações de CRUD devem alertar o usuário sobre as falhas pontuais.

  async submitUser() {
    this.isSubmitting = true;
    try {
      const payload = {
        ...this.userForm,
        role_ids: this.userForm.role_ids.map(Number),
      };
      if (this.editMode) {
        await axios.put(`/api/admin/users/${this.editId}`, payload);
        showAlert("Administrador atualizado!", "success");
      } else {
        await axios.post("/api/admin/users", payload);
        showAlert("Administrador criado!", "success");
      }
      await this.loadUsersTable();
      this.isUserModalOpen = false;
    } catch (error) {
      handleRequestError(error, "Erro ao guardar.");
    } finally {
      this.isSubmitting = false;
    }
  },

  async submitRole() {
    this.isSubmitting = true;
    try {
      const payload = {
        ...this.roleForm,
        permission_ids: this.roleForm.permission_ids.map(Number),
      };
      if (this.editMode) {
        await axios.put(`/api/admin/admin-roles/${this.editId}`, payload);
        showAlert("Cargo atualizado!", "success");
      } else {
        await axios.post("/api/admin/admin-roles", payload);
        showAlert("Cargo criado!", "success");
      }
      await this.loadRolesList();
      this.isRoleModalOpen = false;
    } catch (error) {
      handleRequestError(error, "Erro ao guardar.");
    } finally {
      this.isSubmitting = false;
    }
  },

  async submitPermission() {
    this.isSubmitting = true;
    try {
      if (this.editMode) {
        await axios.put(
          `/api/admin/admin-permissions/${this.editId}`,
          this.permissionForm,
        );
        showAlert("Permissão atualizada!", "success");
      } else {
        await axios.post("/api/admin/admin-permissions", this.permissionForm);
        showAlert("Permissão criada!", "success");
      }
      await this.loadPermissionsList();
      this.isPermissionModalOpen = false;
    } catch (error) {
      handleRequestError(error, "Erro ao guardar.");
    } finally {
      this.isSubmitting = false;
    }
  },

  // ================= DELETE ACTIONS =================

  async deleteEntity(type, id, url, loadFn) {
    const result = await showConfirm(
      `Excluir ${type}`,
      "Tem certeza? Esta ação não pode ser desfeita.",
    );
    if (result.isConfirmed) {
      try {
        await axios.delete(url);
        await this[loadFn]();
        showAlert(`${type} removido com sucesso.`, "success");
      } catch (error) {
        handleRequestError(error, `Erro ao excluir.`);
      }
    }
  },
});
